"use client"

import { useEffect, useMemo, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { parseDocEnvelopes, parseProviderConfigData } from "@/lib/global-config"
import { payloadFieldLabel } from "@/lib/payload-fields"
import { CompanyMapping, GROUP_NAMES, MigrationDetail, ProviderConfigurationData } from "@/types/payroll"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/translations"
import { Loader2 } from "lucide-react"

interface Props {
  detail: MigrationDetail
  onClose: () => void
  /** Called after a successful save (with or without sending). */
  onSaved: () => void
}

const NO_COMPANY = "__none__"

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

/**
 * Why there is nothing to edit, phrased as the actual next step. The record's
 * stored error text is the only signal the client has, so this matches on
 * distinctive fragments of the backend's fail-loud messages (display-only —
 * an unrecognised message falls back to the generic guidance). The fragments
 * matched are the BACKEND's English wording and must not be translated; the
 * guidance returned is.
 */
function noPayloadGuidance(detail: MigrationDetail, t: Translate): string {
  const error = detail.error ?? detail.lastProcessingError ?? ""

  if (error.includes("is not enabled for provider")) {
    return t("editDetails.noPayload.typeNotEnabled", { provider: detail.provider })
  }
  if (error.includes("internal organizations")) {
    return t("editDetails.noPayload.multipleOrgs")
  }
  if (error.includes("no payroll provider") || error.includes("is disabled for this tenant")) {
    return t("editDetails.noPayload.noProvider")
  }
  if (detail.status === "WaitingForWorkOrder") {
    return t("editDetails.noPayload.waitingForWorkOrder")
  }
  return t("editDetails.noPayload.generic")
}

interface FieldDef {
  key: string
  type?: "date"
  hintKey?: TranslationKey
  /** Span both form columns. */
  wide?: boolean
  /** A fixed character limit, whatever the provider. */
  maxLength?: number
  /** A name field: the provider's own name limit applies, when it has one. */
  isName?: boolean
  /** Extra hint shown only when the provider does cap name length — it describes the automatic split. */
  limitedHintKey?: TranslationKey
}

/**
 * The provider's per-name-field limit, or undefined when it has none — the
 * client-side mirror of SendReadiness.NameLengthLimit. EmployDrive caps each
 * name at 25 characters; nothing enforces that for ADP, so the input must not
 * refuse a longer legal name on an ADP tenant.
 */
function nameLimitFor(provider: string): number | undefined {
  return provider.toLowerCase() === "employdrive" ? 25 : undefined
}

/** Every scalar payload field an operator may edit, grouped for the form. The company select and its code fields are rendered separately. */
const FIELD_GROUPS: { titleKey: TranslationKey; fields: FieldDef[] }[] = [
  {
    titleKey: "editDetails.group.whoTheyAre",
    fields: [
      {
        key: "providerEmployeeId",
        wide: true,
        hintKey: "editDetails.hint.providerEmployeeId",
      },
      { key: "firstName", isName: true },
      { key: "lastName", isName: true, limitedHintKey: "editDetails.hint.lastName" },
      {
        key: "middleName",
        isName: true,
        hintKey: "editDetails.hint.middleName",
        limitedHintKey: "editDetails.hint.middleNameSplit",
      },
    ],
  },
  {
    titleKey: "editDetails.group.howToReach",
    fields: [{ key: "email" }, { key: "homePhone" }, { key: "mobilePhone" }],
  },
  {
    titleKey: "editDetails.group.theirWork",
    fields: [
      { key: "jobTitle" },
      { key: "hireDate", type: "date", hintKey: "editDetails.hint.hireDate" },
      { key: "payType", hintKey: "editDetails.hint.payType" },
      { key: "hourlyRate" },
      { key: "normalHours" },
      { key: "department" },
      { key: "workersCompCode" },
      { key: "workLocation", hintKey: "editDetails.hint.workLocation" },
      { key: "ein", hintKey: "editDetails.hint.ein" },
    ],
  },
  {
    titleKey: "editDetails.group.address",
    fields: [
      { key: "address1" },
      { key: "address2" },
      { key: "city" },
      { key: "provinceOrState" },
      { key: "postalOrZip" },
      { key: "countryCode" },
    ],
  },
]

const ALL_FIELD_KEYS = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key))

/**
 * Form-based editor over the worker's outgoing details — the everyday
 * alternative to raw JSON. Edited fields are merged over the existing payload
 * so anything the form does not surface (standard values, organization
 * assignments) is carried through untouched.
 */
export function EditDetailsDialog({ detail, onClose, onSaved }: Props) {
  const { adminApi, payrollApi } = useApi()
  const { toast } = useToast()
  const { t } = useTranslation()

  // Per record, because the limit belongs to the provider this worker goes to.
  const nameMaxLength = useMemo(() => nameLimitFor(detail.provider), [detail.provider])

  const payload = (detail.requestPayload ?? null) as Record<string, unknown> | null

  const [fields, setFields] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      ALL_FIELD_KEYS.map((key) => [key, payload?.[key] === null || payload?.[key] === undefined ? "" : String(payload?.[key])])
    )
  )
  const [companyId, setCompanyId] = useState(
    payload?.internalOrganizationId && Number(payload.internalOrganizationId) > 0
      ? String(payload.internalOrganizationId)
      : NO_COMPANY
  )
  const [legalCode, setLegalCode] = useState(String(payload?.legalCode ?? ""))
  const [payGroup, setPayGroup] = useState(String(payload?.payGroup ?? ""))
  const [providerClientId, setProviderClientId] = useState(String(payload?.providerClientId ?? ""))

  const [providerConfig, setProviderConfig] = useState<ProviderConfigurationData | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [sendAfterSave, setSendAfterSave] = useState(false)

  const setField = (key: string, value: string) => setFields((f) => ({ ...f, [key]: value }))

  // The company choices come from the Providers tab's company list.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const raw = await adminApi.get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.providerConfiguration))
        const match = parseDocEnvelopes(raw)
          .map((d) => parseProviderConfigData(d.data, ""))
          .find((c) => c.provider.toLowerCase() === detail.provider.toLowerCase())
        if (!cancelled) setProviderConfig(match ?? null)
      } catch {
        // Company dropdown degrades to the code fields — the save still works.
        if (!cancelled) setProviderConfig(null)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.provider])

  const companies = useMemo(
    () => (providerConfig?.companyMappings ?? []).filter((m) => !m.isExcluded),
    [providerConfig]
  )

  const applyCompany = (value: string) => {
    setCompanyId(value)
    if (value === NO_COMPANY) return
    const mapping = companies.find((m) => String(m.internalOrganizationId) === value)
    if (mapping) {
      setLegalCode(mapping.legalCode ?? "")
      setPayGroup(mapping.payGroup ?? "")
      setProviderClientId(mapping.providerClientId ?? providerConfig?.providerClientId ?? "")
    }
  }

  const buildEditedPayload = (): Record<string, unknown> | null => {
    if (!payload) return null
    const company: CompanyMapping | undefined =
      companyId === NO_COMPANY ? undefined : companies.find((m) => String(m.internalOrganizationId) === companyId)
    const edited: Record<string, unknown> = { ...payload }
    for (const key of ALL_FIELD_KEYS) {
      const text = fields[key]?.trim() ?? ""
      edited[key] = text === "" ? null : text
    }
    // Identity fields never become null — the backend rejects a mismatch.
    edited.userProfileId = payload.userProfileId
    edited.providerEmployeeId = (fields.providerEmployeeId ?? "").trim()
    edited.legalCode = legalCode.trim() || null
    edited.payGroup = payGroup.trim() || null
    edited.providerClientId = providerClientId.trim()
    if (company) {
      edited.internalOrganizationId = company.internalOrganizationId
      edited.internalOrganizationCode = company.internalOrganizationCode
    }
    return edited
  }

  const save = async (send: boolean) => {
    const edited = buildEditedPayload()
    if (!edited) return

    setIsSaving(true)
    setSendAfterSave(send)
    try {
      if (send) {
        await payrollApi.post(
          API_ENDPOINTS.PAYROLL.MIGRATION_EDIT_RETRY(detail.provider, detail.userProfileId, detail.internalOrganizationId),
          edited
        )
        toast({ title: t("editDetails.toast.savedAndSending") })
      } else {
        await payrollApi.put(
          API_ENDPOINTS.PAYROLL.MIGRATION_SAVE_PAYLOAD(detail.provider, detail.userProfileId, detail.internalOrganizationId),
          edited
        )
        toast({ title: t("editDetails.toast.saved"), description: t("editDetails.toast.savedDescription") })
      }
      onSaved()
    } catch (err) {
      toast({
        variant: "destructive",
        title: send ? t("editDetails.toast.sendFailed") : t("editDetails.toast.saveFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const workerName = detail.workerName || t("editDetails.fallbackWorker", { id: detail.userProfileId })

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:w-[560px] sm:max-w-[92vw]">
        <SheetHeader className="pr-10">
          <SheetTitle>{t("editDetails.title", { name: workerName })}</SheetTitle>
          <SheetDescription>{t("editDetails.description", { provider: detail.provider })}</SheetDescription>
        </SheetHeader>

        {!payload ? (
          <p className="flex-1 px-6 py-4 text-sm text-muted-foreground">{noPayloadGuidance(detail, t)}</p>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {FIELD_GROUPS.map((group) => (
              <section key={group.titleKey} className="space-y-2">
                <h4 className="th-section-label">{t(group.titleKey)}</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {group.fields.map((field) => {
                    const maxLength = field.isName ? nameMaxLength : field.maxLength
                    const hint = [
                      field.hintKey ? t(field.hintKey) : null,
                      field.limitedHintKey && maxLength ? t(field.limitedHintKey) : null,
                      maxLength ? t("editDetails.maxLengthHint", { count: maxLength }) : null,
                    ]
                      .filter(Boolean)
                      .join(" ")
                    return (
                      <div key={field.key} className={field.wide ? "space-y-1 sm:col-span-2" : "space-y-1"}>
                        <Label htmlFor={`ed-${field.key}`}>{payloadFieldLabel(field.key, t)}</Label>
                        <Input
                          id={`ed-${field.key}`}
                          type={field.type ?? "text"}
                          value={fields[field.key] ?? ""}
                          maxLength={maxLength}
                          onChange={(e) => setField(field.key, e.target.value)}
                        />
                        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
                      </div>
                    )
                  })}
                </div>
              </section>
            ))}

            <section className="space-y-2">
              <h4 className="th-section-label">{t("editDetails.group.orgCodes")}</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="ed-company">{t("editDetails.internalOrganization")}</Label>
                  <Select value={companyId} onValueChange={applyCompany}>
                    <SelectTrigger id="ed-company">
                      <SelectValue placeholder={t("common.chooseOrganization")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_COMPANY}>{t("common.notSet")}</SelectItem>
                      {companies.map((m) => (
                        <SelectItem key={m.internalOrganizationId} value={String(m.internalOrganizationId)}>
                          {m.internalOrganizationName || m.internalOrganizationCode || `#${m.internalOrganizationId}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{t("editDetails.orgHint")}</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ed-client-id">{t("field.providerClientId")}</Label>
                  <Input id="ed-client-id" value={providerClientId} onChange={(e) => setProviderClientId(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ed-legal-code">{t("field.legalCode")}</Label>
                  <Input id="ed-legal-code" value={legalCode} onChange={(e) => setLegalCode(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ed-pay-group">{t("field.payGroup")}</Label>
                  <Input id="ed-pay-group" value={payGroup} onChange={(e) => setPayGroup(e.target.value)} />
                </div>
              </div>
            </section>

            <p className="text-xs text-muted-foreground">{t("editDetails.carriedThrough")}</p>
          </div>
        )}

        <SheetFooter>
          {payload && (
            <>
              <Button className="rounded-full" onClick={() => void save(true)} disabled={isSaving}>
                {isSaving && sendAfterSave && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t("editDetails.saveAndSend")}
              </Button>
              <Button className="rounded-full" variant="secondary" onClick={() => void save(false)} disabled={isSaving}>
                {isSaving && !sendAfterSave && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                {t("editDetails.saveForLater")}
              </Button>
            </>
          )}
          <Button className="rounded-full" variant="ghost" onClick={onClose} disabled={isSaving}>
            {t("common.cancel")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
